import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  NgModule,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { VerticalInfosComponentModule } from '../vertical-infos/vertical-infos.component';
import { ToolBarComponentModule } from '../tool-bar/tool-bar.component';
import {
  IcilaluneLogoComponent,
  IcilaluneLogoComponentModule,
} from '../icilalune-logo/icilalune-logo.component';
import { MainMenuComponentModule } from '../main-menu/main-menu.component';
import { Tree } from '@nx-icilalune/drupal-types';
import { MainMenuTreeItem } from '@nx-icilalune/data/icilalune/types';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { ComponentStore } from '@ngrx/component-store';
import { distinctUntilChanged, map, Observable, tap } from 'rxjs';
import { isScullyRunning } from "@scullyio/ng-lib";

interface MainHeaderComponentStore {
  scrollIsTop: boolean;
}

@Component({
  selector: 'sh-ui-main-header',
  template: `
    <sh-ui-icilalune-logo
      #logoComponent
      [splitMode]="logoSplitMode"
    ></sh-ui-icilalune-logo>
    <sh-ui-main-menu
      #mainMenuComponent
      [class.active]="logoSplitMode"
      [menuItems]="mainMenu?.items"
    ></sh-ui-main-menu>
  `,
  styleUrls: ['./main-header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainHeaderComponent extends ComponentStore<MainHeaderComponentStore> {
  @ViewChild('logoComponent') logoComponent!: IcilaluneLogoComponent;
  @ViewChild('mainMenuComponent', { read: ElementRef })
  mainMenuComponent!: ElementRef<HTMLElement>;

  #logoSplitModeTimeOut: any;

  scrollIsTop$ = this.select((s) => s.scrollIsTop);

  #logoSplitMode!: boolean | undefined;
  @Input()
  set logoSplitMode(value: boolean | undefined) {
    this.#logoSplitMode = value;
    if (this.logoComponent) {
      this.logoComponent.splitMode = this.#logoSplitMode;
    }
    if (this.mainMenuComponent) {
      this.mainMenuComponent.nativeElement.classList.remove('active');
      if (this.#logoSplitMode) {
        this.mainMenuComponent.nativeElement.classList.add('active');
      }
    }
    if (this.#logoSplitMode === false) {
      if(!isScullyRunning()){
        this.#logoSplitModeTimeOut = setTimeout(() => {
          if (this.#logoSplitMode === false) {
            this.logoSplitMode = true;
          }
        }, 3000);
      }
    } else {
      if (this.#logoSplitModeTimeOut) {
        clearTimeout(this.#logoSplitModeTimeOut);
      }
    }
  }

  get logoSplitMode(): boolean | undefined {
    return this.#logoSplitMode;
  }

  @Input()
  mainMenu!: Tree<MainMenuTreeItem> | undefined;

  scrollIsTop = this.effect((scrollIsTop$: Observable<boolean>) => {
    return scrollIsTop$.pipe(
      tap((scrollIsTop) => {
        if (scrollIsTop) {
          this.el.nativeElement.classList.add('scroll-is-top');
        } else {
          this.el.nativeElement.classList.remove('scroll-is-top');
        }
      })
    );
  });

  constructor(
    @Inject(DOCUMENT)
    private document: Document,
    private scrollDispatcher: ScrollDispatcher,
    private el: ElementRef<HTMLElement>
  ) {
    super({ scrollIsTop: true });
    this.scrollIsTop(this.scrollIsTop$);
    const scrollIsTop$ = this.scrollDispatcher
      .ancestorScrolled(this.el.nativeElement, 40)
      .pipe(
        map(() => {
          const window = this.document.defaultView;
          if (window) {
            const { scrollY } = window;
            return { scrollIsTop: scrollY === 0 };
          }
          return { scrollIsTop: true };
        }),
        distinctUntilChanged()
      );
    this.patchState(scrollIsTop$);
  }
}

@NgModule({
  imports: [
    CommonModule,
    VerticalInfosComponentModule,
    ToolBarComponentModule,
    IcilaluneLogoComponentModule,
    MainMenuComponentModule,
  ],
  declarations: [MainHeaderComponent],
  exports: [MainHeaderComponent],
})
export class MainHeaderComponentModule {}
